'use client';

import { EcEnv } from '@castlery/config';
import { Grid, Link, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import { selectDYCampaignData } from '@castlery/modules-dy-domain';
import { DtStack } from '@castlery/modules-tracking-components';
import { FortressImage } from '@castlery/shared-components';
import { useAppSelector } from '@castlery/shared-redux-store';
import { storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc';
import { useEffect, useRef, useState } from 'react';
import { hasRichText } from '../../../utils/rich-text-utils';
import { ImageOrVideo, RichTextTypography } from '../components';
import { ButtonProps } from './../button';
import { selectFontFamily, selectHeaderLevel, selectSubHeaderLevel } from './../config/cursive-material-config';
import { useAnchorScroll } from './../hook/anchor';
import { ImageProps } from './../image';
import { VideoProps } from './../video';
import { VideoWrapper } from './../video/video';

export type BannerProps = {
  blok: {
    component: 'full-width-banner';
    _uid?: string;
    button?: ButtonProps[];
    image?: ImageProps[];
    video?: VideoProps[];
    klaviyo_signup_form?: any[];
    header?: string;
    header_level?: 'h1' | 'h2';
    enlarge_header?: boolean;
    header_color?: string;
    sub_header?: string;
    sub_header_level?: 'h2' | 'h3';
    sub_header_color?: string;
    description?: string;
    size?: 'large' | 'medium' | 'small';
    bg_color?: string;
    text_align?: 'left' | 'center' | 'right';
    anchor_link?: string;
    banner_selector_name?: string;
  };
};

function FullWidthBanner({ blok }: BannerProps) {
  const { desktop } = useBreakpoints();
  const {
    _uid,
    size = 'large',
    header,
    header_level,
    enlarge_header,
    header_color,
    sub_header,
    sub_header_level,
    sub_header_color,
    description,
    bg_color,
    text_align = 'center',
    image = [],
    video = [],
    button = [],
    anchor_link,
    klaviyo_signup_form = [],
    banner_selector_name,
  } = blok || {};

  const dyCampaignData = useAppSelector(selectDYCampaignData);
  const bannerData = dyCampaignData[banner_selector_name || ''];
  // const bannerData = {
  //   type: 'video',
  //   link: '/lunar-new-year',
  //   desktop: {
  //     ratio: 0.52,
  //     src: '',
  //     video: 'v1747278688/marketing/SG/homepage/AUSG_Homepage_Banner_S2_25_Desktop_24s',
  //   },
  //   mobile: {
  //     ratio: 1.25,
  //     src: '',
  //     video: 'v1747278686/marketing/SG/homepage/AUSG_Homepage_Banner_S2_25_Mobile_24s',
  //   },
  //   tablet: {
  //     ratio: 0.65,
  //     src: '',
  //     video: 'v1747278685/marketing/SG/homepage/AUSG_Homepage_Banner_S2_25_Tablet_24s',
  //   },
  // };
  const [stackWidth, setStackWidth] = useState(0);
  const [stackHeight, setStackHeight] = useState(900);
  const [bannerHeight, setBannerHeight] = useState(900);
  const blokRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (blokRef.current) {
        const width = blokRef.current.offsetWidth;
        setBannerHeight(Math.min(window.innerWidth, 1728) * (desktop ? 0.52 : 1.25));
        setStackWidth(width);
      }
    };

    requestAnimationFrame(() => {
      updateWidth();

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setStackWidth(entry.contentRect.width);
        }
      });

      if (blokRef.current) {
        resizeObserver.observe(blokRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    });
  }, []);

  useEffect(() => {
    if (bannerData) {
      setBannerHeight(
        Math.min(window.innerWidth, 1728) * (desktop ? bannerData.desktop.ratio : bannerData.mobile.ratio)
      );
    }
  }, [bannerData, stackWidth, desktop]);
  useAnchorScroll({
    ref: blokRef,
    anchorLink: anchor_link,
  });

  const pxConfig = {
    large: 36,
    medium: 12,
    small: 7,
  };
  const alignConfig = {
    left: 'start',
    center: 'center',
    right: 'end',
  };
  const aspectRatioConfig = {
    large: 0.5283,
    medium: 0.2881,
    small: 0.1921,
  };

  const heightConfig = {
    large: 900,
    medium: 675,
    small: 450,
  };

  const addFormatForMp4 = (url: string) => {
    const insertion = 'f_auto,q_auto,w_1280,c_fill/';
    return url.replace(/(\/private\/)/, '$1' + insertion);
  };

  const transferMp4toJpg = (url: string) => {
    return url.replace(/(mp4)/, 'jpg');
  };

  const handleImgSrc = (src: string) => {
    if (src.indexOf('https://') > -1) {
      return src;
    }
    return `https://res.cloudinary.com/castlery/image/upload${src}`;
  };

  const handleVideoSrc = (src: string) => {
    if (src.indexOf('https://') > -1) {
      return src;
    }
    if (src.indexOf('mp4') > -1) {
      if (src.indexOf('https://') === -1) {
        return `https://res.cloudinary.com/castlery/video/upload/${src}.mp4`;
      }
      return src;
    }
    return `https://res.cloudinary.com/castlery/video/upload/${src}.mp4`;
  };

  return (
    <DtStack
      useImpression
      {...storyblokEditable(blok)}
      componentName="full-width-banner"
      uid={_uid}
      key={_uid}
      sx={() => ({
        position: 'relative',
        height: bannerData ? bannerHeight : desktop ? heightConfig[size] : 600,
        ...(banner_selector_name !== '' && {
          minHeight: bannerHeight,
        }),
      })}
      id={anchor_link?.slice(1)}
    >
      <Stack sx={{ height: '100%' }} ref={blokRef}>
        {(video.length > 0 || image.length > 0) && !bannerData && (
          <Stack
            sx={() => ({
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              div: {
                height: '100%',
                paddingTop: '0 !important',
              },
            })}
          >
            <ImageOrVideo
              video={video}
              image={image}
              loader={{
                ratio: !desktop ? 0.65 : aspectRatioConfig[size],
              }}
              sizes={['1-xs', '1-md', '1-lg', '0.8-xl']}
            />
          </Stack>
        )}

        {bannerData && (
          <Link
            href={`${EcEnv.NEXT_PUBLIC_ONEPIECE_HOST}/${EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase()}${
              bannerData.link
            }`}
            sx={() => ({
              display: 'block',
              width: '100%',
              height: '100%',
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 2,
            })}
          >
            <Stack
              sx={() => ({
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
              })}
            >
              {bannerData.type === 'image' && (
                <FortressImage
                  src={handleImgSrc(desktop ? bannerData.desktop.src : bannerData.mobile.src)}
                  ratio={1 / (desktop ? bannerData.desktop.ratio : bannerData.mobile.ratio)}
                  alt={'HP Banner Image'}
                  objectFit="cover"
                  needPreload={true}
                  sizes={['1-xs', '1-md', '1-lg', '0.8-xl']}
                  lazy={false}
                />
              )}
              {bannerData.type === 'video' && (
                <VideoWrapper
                  blok={{
                    desktop_url: bannerData.desktop.video,
                    mobile_url: bannerData.mobile.video,
                    tablet_url: bannerData.tablet?.video || bannerData.mobile.video,
                    autoplay: true,
                    controls: false,
                  }}
                  loader={{
                    ratio: desktop ? bannerData.desktop.ratio : bannerData.mobile.ratio,
                  }}
                />
              )}
            </Stack>
          </Link>
        )}
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems={alignConfig[text_align]}
          sx={(theme) => ({
            flexWrap: 'nowrap',
            gap: '24px',
            px: desktop ? '60px' : '24px',
            py: desktop ? `${pxConfig[size] * 4}px` : '149px',
            height: '100%',
            ...(video.length > 0 || image.length > 0
              ? {
                  zIndex: 1,
                }
              : {
                  backgroundColor: bg_color || theme.palette.brand.maroonVelvet[500],
                }),
          })}
        >
          {sub_header && (
            <Typography
              textAlign={text_align}
              level={selectSubHeaderLevel(sub_header_level || 'subh2')}
              sx={(theme) => ({
                color: sub_header_color || theme.palette.brand.warmLinen[500],
                fontFamily: theme.fontFamily.body,
                [theme.breakpoints.up('sm')]: {
                  maxWidth: '828px',
                },
              })}
            >
              {sub_header}
            </Typography>
          )}

          {header && (
            <Typography
              textAlign={text_align}
              level={selectHeaderLevel(header_level || 'h1')}
              sx={(theme) => ({
                color: header_color || theme.palette.brand.warmLinen[500],
                ...(enlarge_header && {
                  fontSize: {
                    xs: '36px',
                    md: '52px',
                  },
                }),
                [theme.breakpoints.up('sm')]: {
                  maxWidth: '828px',
                },
                fontFamily: selectFontFamily('h1'),
              })}
            >
              {header}
            </Typography>
          )}

          {hasRichText(description) && (
            <RichTextTypography
              textAlign={text_align}
              level="body1"
              sx={(theme) => ({
                color: theme.palette.brand.warmLinen[500],
                [theme.breakpoints.up('sm')]: {
                  maxWidth: '828px',
                },
              })}
              description={description}
            />
          )}

          {button?.length > 0 && (
            <Stack>
              {button.map((nestedBlok) => (
                <StoryblokServerComponent
                  blok={nestedBlok}
                  key={nestedBlok._uid}
                  color={nestedBlok.color}
                  textColor={nestedBlok.text_color}
                />
              ))}
            </Stack>
          )}
          {klaviyo_signup_form?.length > 0 && (
            <Stack
              sx={{
                width: desktop ? '520px' : '342px',
              }}
            >
              {klaviyo_signup_form.map((nestedBlok) => {
                return <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />;
              })}
            </Stack>
          )}
        </Grid>
      </Stack>
    </DtStack>
  );
}

export { FullWidthBanner };
